import { useQuery } from '@tanstack/react-query';
import type { Readiness } from '@geotracker/contracts';
import { Card, StatTile } from '@/components/ui/card';
import { Dot } from '@/components/ui/badge';
import { ErrorState, Loading } from '@/components/ui/state';

/**
 * Operator view: is the chain up, and how fast is each link answering.
 *
 * This proves the whole path end to end — panel → Vite proxy → Fastify →
 * Postgres/Redis — and stays useful afterwards, because "the map is empty" and
 * "Redis is down" look identical from the tracking screen.
 */
export function DashboardPage() {
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['health', 'ready'],
    // Health is served outside the /api/v1 prefix.
    queryFn: async () => {
      const res = await fetch('/health/ready');
      return (await res.json()) as { data: Readiness };
    },
    refetchInterval: 15_000,
  });

  if (isPending) return <Loading label="Checking system health…" />;
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;

  const readiness = data.data;
  const tone =
    readiness.status === 'ok' ? 'success' : readiness.status === 'degraded' ? 'warning' : 'danger';

  return (
    <section className="space-y-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">System health</h1>
          <p className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground">
            <Dot tone={tone} pulse={readiness.status === 'ok'} />
            <span className="capitalize">{readiness.status}</span> · version {readiness.version} ·
            refreshed every 15s
          </p>
        </div>
      </header>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {Object.entries(readiness.checks).map(([name, check]) => (
          <StatTile
            key={name}
            label={name}
            value={check.latencyMs === undefined ? '—' : `${check.latencyMs}ms`}
            tone={check.status === 'up' ? 'default' : 'danger'}
            sub={
              check.message ? (
                <span className="text-warning">{check.message}</span>
              ) : (
                <span className="capitalize">{check.status}</span>
              )
            }
            icon={<Dot tone={check.status === 'up' ? 'success' : 'danger'} />}
          />
        ))}
      </div>

      <Card className="p-4">
        <h2 className="text-sm font-semibold">What these numbers mean</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Latency is a round trip from this API process to the dependency, measured on the health
          check itself. Postgres climbing above a few hundred milliseconds while ingest is running
          usually means the write path is queuing behind a long-running query; Redis doing the same
          affects the live map before it affects anything else, since every position on it is read
          from there.
        </p>
      </Card>
    </section>
  );
}
